'use client'

import { useEffect, useRef, useState } from 'react'

/**
 * Odometer-Style Counter. Zählt beim Sichtbar-Werden in 1.4 s auf den Zielwert.
 *
 * R54 (2026-05-16): Bug-Fix — `started.current=true` blockierte alle weiteren
 * Animationen, sodass async geladene API-Daten (Hero-Stats) den Wert nicht mehr
 * verändern konnten. Frontseite zeigte permanent 0. Jetzt:
 *  - Initial: IntersectionObserver wartet bis sichtbar, dann animiert auf value
 *  - Wenn value sich danach noch ändert (z.B. fetch lädt fertig): direkte
 *    Re-Animation von current display → neuer target, kein Block.
 */
export function CountUp({
  value,
  duration = 1400,
  decimals = 0,
  suffix = '',
  prefix = '',
  className = '',
}: {
  value: number
  duration?: number
  decimals?: number
  suffix?: string
  prefix?: string
  className?: string
}) {
  const ref = useRef<HTMLSpanElement | null>(null)
  const [display, setDisplay] = useState(0)
  const lastAnimatedTo = useRef<number>(0)
  const initialDone = useRef(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    let raf = 0
    let cancelled = false

    function animate(target: number) {
      const startValue = lastAnimatedTo.current
      const startTs = performance.now()
      const ease = (t: number) => 1 - Math.pow(1 - t, 3)
      function tick(now: number) {
        if (cancelled) return
        const t = Math.min(1, (now - startTs) / duration)
        const v = startValue + (target - startValue) * ease(t)
        setDisplay(v)
        if (t < 1) {
          raf = requestAnimationFrame(tick)
        } else {
          setDisplay(target)
          lastAnimatedTo.current = target
        }
      }
      raf = requestAnimationFrame(tick)
    }

    if (initialDone.current) {
      // value-Update NACH der ersten Animation → direkt animieren ohne Observer
      animate(value)
      return () => {
        cancelled = true
        cancelAnimationFrame(raf)
      }
    }

    // Initial: warte bis sichtbar, dann animieren
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !initialDone.current) {
          initialDone.current = true
          animate(value)
          io.unobserve(el)
        }
      },
      { threshold: 0.4 },
    )
    io.observe(el)
    return () => {
      cancelled = true
      cancelAnimationFrame(raf)
      io.disconnect()
    }
  }, [value, duration])

  const formatted = display.toLocaleString('de-DE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })

  return (
    <span ref={ref} className={className}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  )
}
